import { type BookDataService } from "core/Services/BookDataService";
import { type Modal } from "shared/Modal";
import { BookModal } from "core/Controls/Books/BookModal";
import { type SearchBooksService } from "core/Services/SearchBooksService";
import { type DownloadBookService } from "core/Services/DownloadBookService";
import { type BookReviewService } from "core/Services/BookReviewService";
import { type ReadBookService } from "core/Services/ReadBookService";
import { type BookARQuizService } from "core/Services/BookARQuizService";
import { type PermissionService } from "core/Services/PermissionService";
import { InlineToastMessage } from "shared/Components/ToastMessage";
import { type BookBundleService } from "shared/Services/BookBundleService";
import { Role } from "shared/Api/Role";
import { type BookBagService } from "shared/Services/BookBagService";
import { type MyonLanguageLoader } from "Lang";
import { type BookMinimumGradeAccessService } from "core/Services/BookMinimumGradeAccessService";
import { isHTMLElement, mustBeHTMLElement } from "silver";

function makeIcon(className: string): HTMLElement {
	const icon = document.createElement("i");
	icon.className = className;
	return icon;
}

type ShowBookModalOptions = {
	projectId: number;
	source: HTMLElement;
	callingBand: number;
}

export class BookModalService {

	constructor(
		private bookBundleService: BookBundleService,
		private bookBagService: BookBagService,
		private bookMinimumGradeAccessService: BookMinimumGradeAccessService,
		private bookDataService: BookDataService,
		private bookARQuizService: BookARQuizService,
		private searchBooksService: SearchBooksService,
		private downloadBookService: DownloadBookService,
		private readBookService: ReadBookService,
		private bookReviewService: BookReviewService,
		private permissionService: PermissionService,
		private modal: Modal,
		private userId: number,
		private role: Role,
		private canManageBookGradeLevelRestrictions: boolean,
		private showMyList: boolean,
		private lang: MyonLanguageLoader,
	) { }

	public async showBookModal(bookId: number, options: Partial<ShowBookModalOptions> = { }): Promise<void> {

		const isStudent = this.role === Role.STUDENT;

		const promises = Promise.all([
			this.bookDataService.getFromId(bookId),
			this.lang.load("Books"),
			this.lang.load("User")
		]);

		const [ book, bookLang, userLang ] = await promises;

		const modal = new BookModal(
			this.bookReviewService,
			this.searchBooksService,
			this.bookMinimumGradeAccessService,
			book,
			this.userId,
			this.role,
			this.canManageBookGradeLevelRestrictions,
			this.permissionService.isSharedLogin(),
			bookLang,
			userLang,
		);

		modal.addAction({
			label:         document.createTextNode(bookLang.ACTION_OPEN()),
			icon:          makeIcon("mycon -read-book-large"),
			modifierClass: "-read",
			action:        (e) => {
				this.readBookService.readBookFromEvent(book.archiveCode, e, {
					projectId:   options.projectId,
					callingBand: options.callingBand
				});
			}
		}, true);

		if (this.downloadBookService.available) {
			const iconClass = isStudent ? "mycon -download-to-device-large" : "mycon -download-to-device";

			modal.addAction({
				label:         document.createTextNode(bookLang.ACTION_DOWNLOAD()),
				icon:          makeIcon(iconClass),
				modifierClass: "-download",
				action:        () => this.downloadBookService.downloadBook(bookId),
			}, isStudent);
		}

		if (this.showMyList) {
			const myListLabel = document.createTextNode("");
			const myListIcon = makeIcon("");

			const toggleOnMyList = (onMyList: boolean, button: HTMLElement) => {
				myListIcon.className = onMyList ? "mycon -mylist-remove-large" : "mycon -mylist-add-large";
				myListLabel.textContent = onMyList ? bookLang.STATUS_FAVORITED() : bookLang.ACTION_ADD_LONG();
				button?.classList.toggle("-removeFromList", onMyList);
				button?.classList.toggle("-addToList", !onMyList);
			};

			const actionButton = modal.addAction({
				label:         myListLabel,
				icon:          myListIcon,
				modifierClass: "",
				action:        () => this.bookBundleService.setBookIdsInFavorites([ bookId ], !book.onMyList, modal)
			}, true);

			this.bookBundleService.onBeforeFavoriteUpdate(({ bookId, favorited }) => {
				if (bookId === book.bookId) {
					toggleOnMyList(favorited, actionButton);
				}
			});

			toggleOnMyList(book.onMyList, actionButton);
		}


		if (isStudent) {
			this.bookARQuizService.isBookAndUserligible(book, this.userId, options.projectId)
				.then(isEligible => {
					modal.showArQuizButton(isEligible);
				});
			modal.onRequestArQuizLaunch(e => {
				let sender: HTMLElement|undefined;
				if (e.originalEvent && isHTMLElement(e.originalEvent.target)) {
					sender = e.originalEvent.target;
				}
				this.bookARQuizService.openArQuiz(bookId, {
					projectId: options.projectId,
					source:    sender,
					target:    "_blank"
				});
			});

		} else {
			modal.addAction({
				label:         document.createTextNode(bookLang.ACTION_ADD_TO_BOOK_BAG()),
				icon:          makeIcon("mycon -book-bag-empty"),
				modifierClass: "-addToBookBag",
				action:        (e) => {
					const count = this.bookBagService.getBookIds().length;
					this.bookBagService.addBookIds([ bookId ]);

					if (count > 0) {
						return new InlineToastMessage(mustBeHTMLElement(e.currentTarget), bookLang.ADDED_TO_BOOK_BAG());
					}
					this.modal.back(true);
				}
			}, true);

			modal.addAction({
				label:         document.createTextNode(bookLang.ACTION_COPY_LINK()),
				icon:          makeIcon("mycon -link"),
				modifierClass: "",
				action:        (e: Event) => {
					navigator.clipboard.writeText(this.readBookService.getBookUrl(book.archiveCode));
					return new InlineToastMessage(mustBeHTMLElement(e.currentTarget), bookLang.LINK_COPIED());
				}
			});
		}

		modal.onClose(() => this.modal.back(true));
		await this.modal.show(modal.element, {
			marginTop:   10,
			dismissable: true,
			source:      options.source
		});
	}
}
